import React, { useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { Pie } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSchema from '../../FAQSchema';
import AdBanner from '../AdPage/AdBanner';

// Register necessary components
ChartJS.register(
  ArcElement,
  ChartTooltip,
  Legend
);

const SbihomeLoanEmiCalculator      = () => {
   const [loanAmount, setLoanAmount] = useState(5000000);
    const [interestRate, setInterestRate] = useState(7.5);
    const [loanTenure, setLoanTenure] = useState(20);
    const [monthlyEMI, setMonthlyEMI] = useState(0);
    const [totalInterest, setTotalInterest] = useState(0);
    const [totalAmount, setTotalAmount] = useState(0);
    const [amortizationSchedule, setAmortizationSchedule] = useState([]);
    const [activeYear, setActiveYear] = useState(null);
  
    const calculateEMI = () => {
      const principal = loanAmount;
      const annualInterest = interestRate / 100;
      const monthlyInterest = annualInterest / 12;
      const tenureInMonths = loanTenure * 12;
  
      const emi =
        (principal * monthlyInterest * Math.pow(1 + monthlyInterest, tenureInMonths)) /
        (Math.pow(1 + monthlyInterest, tenureInMonths) - 1);
  
      const totalAmount = emi * tenureInMonths;
      const totalInterest = totalAmount - principal;
  
      setMonthlyEMI(emi.toFixed(2));
      setTotalInterest(totalInterest.toFixed(2));
      setTotalAmount(totalAmount.toFixed(2));
  
      const schedule = [];
      let balance = principal;
      // let year = new Date().getFullYear();
      let currentYear = new Date().getFullYear();
  
      let currentMonth = 6;
  
      for (let i = 0; i < tenureInMonths; i++) {
        const interestForMonth = balance * monthlyInterest;
        const principalForMonth = emi - interestForMonth;
        balance -= principalForMonth;
  
        schedule.push({
          // month: i + 1,
          // year: year,
          month: (currentMonth + i) % 12 + 1, // Ensure month is in range 1-12
          year: currentYear + Math.floor((currentMonth + i) / 12),
          principalPaid: principalForMonth,
          interestPaid: interestForMonth,
          totalPayment: emi,
          balance: balance,
        });
  
        // if ((i + 1) % 12 === 0) {
        //   year++;
        // }
      }
  
      setAmortizationSchedule(schedule);
    };
  
    const handleLoanAmountChange = (e) => {
      const value = e.target.value;
      setLoanAmount(value === '' ? 0 : parseInt(value));
    };
  
    const handleInterestRateChange = (e) => {
      const value = e.target.value;
      setInterestRate(value === '' ? 0 : parseFloat(value));
    };
  
    const handleLoanTenureChange = (e) => {
      const value = e.target.value;
      setLoanTenure(value === '' ? 0 : parseInt(value));
    };
  
    const handleYearToggle = (year) => {
      setActiveYear(activeYear === year ? null : year);
    };
  
    const data = {
      labels: ['Principal Amount', 'Total Interest'],
      datasets: [
        {
          label: 'EMI Breakdown',
          data: [loanAmount, totalInterest],
          backgroundColor: ['#36A2EB', '#FF6384'],
        },
      ],
    };
    const categories = [
      { id: 1, name: 'Category 1', link: '/' },
      { id: 2, name: 'Category 2', link: '/' },
      { id: 3, name: 'Category 3', link: '/' },
      { id: 4, name: 'Category 4', link: '/' },
      // Add more categories as needed
    ];
  function Icon({ id, open }) {
      return (
          <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
          >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
          </svg>
      );
    }
    
    
      const [activeIndex, setActiveIndex] = useState(0);
    
       
      const faqs = [
        {
            "question": "What is an EMI in a home loan?",
            "answer": "EMI is the monthly repayment amount comprising both principal and interest components you pay to the bank."
          },
          {
            "question": "Can I change the EMI after loan approval?",
            "answer": "Yes, through prepayment or tenure adjustment, your EMI can change."
          },
          {
            "question": "Is the SBI Home Loan EMI Calculator accurate?",
            "answer": "Yes, it uses standard mathematical formulas for precise estimates."
          },
          {
            "question": "Does the calculator show total interest payable?",
            "answer": "Absolutely. It gives you a full view of EMI, interest, and total repayment."
          },
          {
            "question": "Can I use the calculator for top-up home loans?",
            "answer": "Yes, input the top-up amount as the loan amount and apply the new rate."
          },
          {
            "question": "Does the EMI remain constant throughout the loan?",
            "answer": "In most fixed-rate loans, yes. In floating-rate loans, it may vary."
          },
          {
            "question": "How often should I use the EMI calculator?",
            "answer": "You can use it as often as needed to try different loan scenarios."
          },
          {
            "question": "Can I use the calculator before applying for a loan?",
            "answer": "Definitely. It helps you understand repayment before committing."
          },
          {
            "question": "What tenure should I choose for lower EMIs?",
            "answer": "Longer tenures result in lower EMIs, but higher total interest outgo."
          },
          {
            "question": "How does prepayment affect my EMI?",
            "answer": "Prepayment reduces outstanding principal, which may lower your EMI or shorten tenure."
          },
          {
            "question": "Can joint applicants use this calculator?",
            "answer": "Yes. Though EMI won’t change, it helps both applicants estimate the shared responsibility."
          },
          {
            "question": "Is it mandatory to register or sign up to use the calculator?",
            "answer": "No registration is needed. It's a free tool for everyone."
          },
          {
            "question": "Does it consider property insurance and taxes?",
            "answer": "No. It only calculates principal and interest components."
          },
          {
            "question": "Can I print the amortisation schedule?",
            "answer": "Yes, you can export or print the results for future reference."
          },
          {
            "question": "Is TheFinMaster calculator updated with current SBI rates?",
            "answer": "Yes. We regularly update interest rates to reflect SBI's latest offerings."
          }
        ]
      const toggleAccordion = (index) => {
        if (activeIndex === index) {
            // If the clicked question is already expanded, collapse it
            setActiveIndex(null);
        } else {
            // If the clicked question is not expanded, expand it
            setActiveIndex(index);
        };
      }
    return (
      <>
  
  <Helmet>
           <title>SBI Home Loan EMI Calculator – Estimate Your Housing Loan Easily</title>
          <meta  name='description' content='Use the SBI Home Loan EMI Calculator to calculate your monthly repayments. Plan your housing loan with accurate EMI estimates.'/>
          <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/sbi-home-loan-emi-calculator" />
          <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
          <meta name="robots" content="max-video-preview:-1" />
        </Helmet>
        <FAQSchema faqs={faqs} />
        {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
            <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </li>
          <li className="flex items-center">
            <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
            <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </li>
            <li className="flex items-center">
            <span className="text-gray-500 ">SBI Home Loan EMI Calculator</span>
          </li> 
        
        </ol>
      </nav> */}
         <div className="w-full flex justify-center items-center px-4 mt-20">
  <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
  <AdBanner displayOrder="1" />
  </div>
</div>
      <div className='py-4 '>
  
     
      <div className="container mx-auto p-4 shadow-xl rounded-md ">
        <h1 className="text-2xl font-bold mb-4"> SBI Home Loan EMI Calculator</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Loan Amount</label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  value={loanAmount}
                  onChange={handleLoanAmountChange}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">₹ {loanAmount.toLocaleString()}</span>
              </div>
              <input
                type="range"
                min="0"
                max="100000000"
                value={loanAmount}
                onChange={(e) => setLoanAmount(parseInt(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Rate of Interest (P.A.)</label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={interestRate}
                  onChange={handleInterestRateChange}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">{interestRate} %</span>
              </div>
              <input
                type="range"
                min="0"
                max="15"
                step="0.1"
                value={interestRate}
                onChange={(e) => setInterestRate(parseFloat(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 font-semibold">Loan Tenure (Years)</label>
              <div className="flex items-center">
                <input
                  type="number"
                  min="0"
                  max="30"
                  value={loanTenure}
                  onChange={handleLoanTenureChange}
                  className="w-1/2 p-2 border rounded mr-2"
                />
                <span className="text-gray-700">{loanTenure} Yr</span>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                value={loanTenure}
                onChange={(e) => setLoanTenure(parseInt(e.target.value))}
                className="w-full mt-2"
              />
            </div>
            <button
              onClick={calculateEMI}
              className="bg-blue-500 text-white px-4 py-2 rounded"
            >
              Calculate
            </button>
          </div>
          <div>
            <div className="mb-4">
              <h2 className="text-xl font-bold">Results</h2>
              <p>Monthly EMI: ₹ {parseFloat(monthlyEMI).toLocaleString()}</p>
              <p>Total Principal Amount: ₹ {loanAmount.toLocaleString()}</p>
              <p>Total Interest: ₹ {parseFloat(totalInterest).toLocaleString()}</p>
              <p>Total Amount (Principal + Interest): ₹ {parseFloat(totalAmount).toLocaleString()}</p>
            </div>
            <div>
              <h2 className="text-xl font-bold">Breakdown</h2>
              <div className="flex justify-center">
                <Pie data={data} options={{ maintainAspectRatio: false }} width={300} height={300} />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-6">
          <h2 className="text-xl font-bold mb-4">Amortization Schedule</h2>
          {amortizationSchedule.length > 0 && (
            <div className="flex flex-col space-y-4">
              {Array.from(new Set(amortizationSchedule.map(item => item.year))).map((year, idx) => (
                <div key={idx} className="mb-4 border rounded-md shadow-md">
                  <div
                    className={`cursor-pointer text-lg font-semibold bg-gray-100 p-4 flex justify-between items-center ${activeYear === year ? 'bg-blue-100' : ''}`}
                    onClick={() => handleYearToggle(year)}
                  >
                    <span>{year}</span>
                    <span>{activeYear === year ? '-' : '+'}</span>
                  </div>
                  {activeYear === year && (
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white rounded-md shadow-md text-center">
                        <thead>
                          <tr>
                            <th className="py-2 px-4 border-b">Month</th>
                            <th className="py-2 px-4 border-b">Principal Paid</th>
                            <th className="py-2 px-4 border-b">Interest Charged</th>
                            <th className="py-2 px-4 border-b">Total Payment</th>
                            <th className="py-2 px-4 border-b">Balance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {amortizationSchedule.filter(item => item.year === year).map((item, idx) => (
                            <tr key={idx}>
                              <td className="py-2 px-4 border-b">{new Date(0, item.month - 1).toLocaleString('default', { month: 'short' })}</td>
                              <td className="py-2 px-4 border-b">₹ {item.principalPaid.toFixed(2).toLocaleString()}</td>
                              <td className="py-2 px-4 border-b">₹ {item.interestPaid.toFixed(2).toLocaleString()}</td>
                              <td className="py-2 px-4 border-b">₹ {item.totalPayment.toFixed(2).toLocaleString()}</td>
                              <td className="py-2 px-4 border-b">₹ {item.balance.toFixed(2).toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
     <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
     <div className="w-full md:w-2/3 p-4 mx-auto">
     <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            Plan Your SBI Home Loan Payments with TheFinMaster’s EMI Calculator
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            Buying a home is a major milestone, and planning your finances wisely is crucial to making your home ownership journey smooth. The SBI Home Loan EMI Calculator by TheFinMaster is your personalized financial guide, helping you estimate and plan your monthly loan payments efficiently. Whether you're considering purchasing a new flat, building a house, or investing in property, this calculator gives you clarity, precision, and financial foresight.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Home Loan EMI?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              A Home Loan EMI (Equated Monthly Installment) is a fixed payment amount made by a borrower to a lender at a scheduled date each calendar month. It comprises two components – the principal amount and the interest on the outstanding loan. Over the loan tenure, the proportion of the interest and principal amount in each EMI varies. Initially, the interest component is higher, but as repayments progress, the principal component increases.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              EMIs are crucial for effective budgeting. They help you understand how much of your monthly income will go toward repaying your home loan. By planning your EMI accurately, you can ensure your home loan does not interfere with your regular financial obligations.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is the SBI Home Loan EMI Calculator?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SBI Home Loan EMI Calculator is a simple yet highly accurate digital tool provided by TheFinMaster. It is designed to help prospective homeowners get a realistic idea of their monthly repayments before applying for an SBI home loan. All you need to do is input the loan amount, interest rate, and loan tenure. The calculator instantly displays the EMI, total interest payable, and the total repayment amount.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              This calculator eliminates guesswork and gives you a financial edge by helping you visualize your repayment structure. It empowers you to adjust your loan amount or tenure to find a plan that fits your budget. Whether you’re a salaried employee, self-employed individual, or a first-time buyer, this tool is tailored for clarity and convenience.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Why Use TheFinMaster’s SBI Home Loan EMI Calculator?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using TheFinMaster’s SBI Home Loan EMI Calculator offers a range of benefits:
            </p>
            <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Instant EMI Estimates:</strong> Save time by receiving results in real-time. No need for complex manual calculations.</li>
              <li><strong>Transparency:</strong> Know upfront what your EMI commitments will look like based on the current SBI home loan interest rates.</li>
              <li><strong>Better Financial Planning:</strong> Estimate how the home loan fits into your current and future expenses.</li>
              <li><strong>Compare Scenarios:</strong> Change the loan tenure, interest rate, or loan amount to see different EMI outcomes and choose the best combination.</li>
              <li><strong>No Hidden Costs:</strong> The calculator provides a clear, accurate breakdown without any surprise fees or assumptions.</li>
              <li><strong>Helps You Decide:</strong> By calculating different scenarios, you can confidently decide the most feasible loan structure for your situation.</li>
              <li><strong>Credible & Updated:</strong> Powered by TheFinMaster, the calculator reflects the latest interest rates and financial standards.</li>
            </ol>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How Does the SBI Home Loan EMI Calculator Work?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The SBI Home Loan EMI Calculator uses a standard EMI formula:
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              EMI = [P x r x (1+r)^n] / [(1+r)^n - 1]
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Where:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>P = Principal Loan Amount</li>
              <li>r = Monthly Interest Rate (Annual Rate ÷ 12 ÷ 100)</li>
              <li>n = Loan Tenure in Months</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Example:</strong><br />
              Suppose you take a home loan of ₹40,00,000 from SBI at an interest rate of 8.50% per annum for a tenure of 20 years.<br />
              Loan Amount (P): ₹40,00,000<br />
              Interest Rate: 8.50% per annum = 0.00708 monthly<br />
              Tenure: 240 months<br />
              Estimated EMI = ₹34,678<br />
              This example demonstrates how the EMI increases or decreases based on interest rate and tenure.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is the Amortisation Schedule?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The amortisation schedule is a complete table of loan payments, showing the breakdown of each EMI into principal and interest components. Over time, the interest portion decreases while the principal portion increases. This schedule helps you understand how much principal and interest you pay each year, enabling you to plan prepayments or foreclosures better.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>12-Month Amortisation Schedule for ₹40,00,000 at 8.50% (20-year loan)</strong>
            </p>
            {/* <div className="overflow-x-auto">
              <table className="w-full text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-4 border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border px-4 py-2 text-left">Month</th>
                    <th className="border px-4 py-2 text-left">EMI (₹)</th>
                    <th className="border px-4 py-2 text-left">Principal (₹)</th>
                    <th className="border px-4 py-2 text-left">Interest (₹)</th>
                    <th className="border px-4 py-2 text-left">Outstanding Balance (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border px-4 py-2">1</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,045</td>
                    <td className="border px-4 py-2">27,633</td>
                    <td className="border px-4 py-2">39,92,955</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">2</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,095</td>
                    <td className="border px-4 py-2">27,583</td>
                    <td className="border px-4 py-2">39,85,860</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">3</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,145</td>
                    <td className="border px-4 py-2">27,533</td>
                    <td className="border px-4 py-2">39,78,715</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">4</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,196</td>
                    <td className="border px-4 py-2">27,482</td>
                    <td className="border px-4 py-2">39,71,519</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">5</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,247</td>
                    <td className="border px-4 py-2">27,431</td>
                    <td className="border px-4 py-2">39,64,272</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">6</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,298</td>
                    <td className="border px-4 py-2">27,380</td>
                    <td className="border px-4 py-2">39,56,974</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">7</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,350</td>
                    <td className="border px-4 py-2">27,328</td>
                    <td className="border px-4 py-2">39,49,624</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">8</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,402</td>
                    <td className="border px-4 py-2">27,276</td>
                    <td className="border px-4 py-2">39,42,222</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">9</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,454</td>
                    <td className="border px-4 py-2">27,224</td>
                    <td className="border px-4 py-2">39,34,768</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">10</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,507</td>
                    <td className="border px-4 py-2">27,171</td>
                    <td className="border px-4 py-2">39,27,261</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">11</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,560</td>
                    <td className="border px-4 py-2">27,118</td>
                    <td className="border px-4 py-2">39,19,701</td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-2">12</td>
                    <td className="border px-4 py-2">34,678</td>
                    <td className="border px-4 py-2">7,613</td>
                    <td className="border px-4 py-2">27,065</td>
                    <td className="border px-4 py-2">39,12,088</td>
                  </tr>
                </tbody>
              </table>
            </div> */}
            <div className="mt-6">
  {/* Table view for medium and up */}
  <div className="hidden md:block overflow-x-auto">
    <table className="w-full text-lg text-gray-600 mt-4 border-collapse">
      <thead>
        <tr className="bg-gray-100">
          <th className="border px-4 py-2 text-left">Month</th>
          <th className="border px-4 py-2 text-left">EMI Amount (₹)</th>
          <th className="border px-4 py-2 text-left">Principal    (₹)</th>
          <th className="border px-4 py-2 text-left">Interest (₹)</th>
          <th className="border px-4 py-2 text-left">Outstanding Balance  (₹)</th>
        </tr>
      </thead>
      <tbody>
        {[
          
          ["1", "34,678", "7,045", "27,633", "39,92,955"],
  ["2", "34,678", "7,095", "27,583", "39,85,860"],
  ["3", "34,678", "7,145", "27,533", "39,78,715"],
  ["4", "34,678", "7,196", "27,482", "39,71,519"],
  ["5", "34,678", "7,247", "27,431", "39,64,272"],
  ["6", "34,678", "7,298", "27,380", "39,56,974"],
  ["7", "34,678", "7,350", "27,328", "39,49,624"],
  ["8", "34,678", "7,402", "27,276", "39,42,222"],
  ["9", "34,678", "7,454", "27,224", "39,34,768"],
  ["10", "34,678", "7,507", "27,171", "39,27,261"],
  ["11", "34,678", "7,560", "27,118", "39,19,701"],
  ["12", "34,678", "7,613", "27,065", "39,12,088"]
     
        ].map(([month, emi, interest, principal, balance], index) => (
          <tr key={index}>
            <td className="border px-4 py-2">{month}</td>
            <td className="border px-4 py-2">{emi}</td>
            <td className="border px-4 py-2">{interest}</td>
            <td className="border px-4 py-2">{principal}</td>
            <td className="border px-4 py-2">{balance}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>

  {/* Card layout for small screens */}
  <div className="md:hidden space-y-4 mt-4">
    {[
            ["1", "34,678", "7,045", "27,633", "39,92,955"],
            ["2", "34,678", "7,095", "27,583", "39,85,860"],
            ["3", "34,678", "7,145", "27,533", "39,78,715"],
            ["4", "34,678", "7,196", "27,482", "39,71,519"],
            ["5", "34,678", "7,247", "27,431", "39,64,272"],
            ["6", "34,678", "7,298", "27,380", "39,56,974"],
            ["7", "34,678", "7,350", "27,328", "39,49,624"],
            ["8", "34,678", "7,402", "27,276", "39,42,222"],
            ["9", "34,678", "7,454", "27,224", "39,34,768"],
            ["10", "34,678", "7,507", "27,171", "39,27,261"],
            ["11", "34,678", "7,560", "27,118", "39,19,701"],
            ["12", "34,678", "7,613", "27,065", "39,12,088"]
    ].map(([month, emi, interest, principal, balance], index) => (
      <div key={index} className="bg-white shadow rounded-xl p-4 text-sm text-gray-700 space-y-1 border">
        <div><span className="font-semibold">Month:</span> {month}</div>
        <div><span className="font-semibold">EMI Amount (₹):</span> {emi}</div>
        <div><span className="font-semibold"> Principal   (₹):</span> {interest}</div>
        <div><span className="font-semibold">Interest   (₹):</span> {principal}</div>
        <div><span className="font-semibold"> Outstanding Balance (₹):</span> {balance}</div>
      </div>
    ))}
  </div>
</div>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Factors Affecting Your Home Loan EMI
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <ol className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
                <li><strong>Loan Amount:</strong> The total loan you borrow has a direct impact on your EMI. A higher principal loan amount will result in a proportionately larger EMI, especially if the interest rate and tenure remain unchanged.</li>
                <li><strong>Interest Rate:</strong> Even a slight variation in the interest rate—say 0.5%—can lead to a noticeable difference in your monthly EMI and total loan repayment. Choosing between fixed and floating rates also affects long-term EMI stability.</li>
                <li><strong>Loan Tenure:</strong> A longer repayment tenure reduces your monthly EMI burden, making it more affordable month-to-month. However, it increases the total interest outgo over the life of the loan, making the loan more expensive in the long run.</li>
                <li><strong>Processing Fees:</strong> Though it doesn’t directly influence your EMI amount, processing fees add to your upfront cost. It can slightly impact your initial budget and should be considered when calculating total cost of borrowing.</li>
                <li><strong>Prepayments:</strong> Making part-prepayments during the loan tenure helps in reducing the principal amount. This leads to a decrease in the remaining interest payable and future EMIs, or a shorter loan tenure, depending on lender terms.</li>
                <li><strong>Credit Score:</strong> A high credit/CIBIL score (usually 750+) shows financial discipline and increases your chances of securing a lower interest rate. A lower rate means reduced EMIs and overall loan cost.</li>
                <li><strong>Income & Profile:</strong> Lenders evaluate your income stability, job profile, and existing liabilities. A steady and high income with fewer liabilities can fetch you better interest rates and favorable terms, indirectly reducing your EMI burden.</li>
              </ol>
            </p>
          </div>
        </div>

     <div className="w-full md:w-1/3 p-4">
       <div className="bg-white shadow-md rounded-lg overflow-hidden">
         <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
           <h2 className="text-lg font-semibold ">Popular Categories</h2>
         </div>
         <div className="p-2 ">
         <ul>
                                <Link to='/financial-tools-calculators/swp-calculator'>
                                    <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/fd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/rd-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/nps-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>

                                </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>

                                </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>

                                </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                    
                                </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                </Link>
                                {/* {categories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
                            </ul>
                        </div>
                    </div>

                          {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
                    <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                        <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                            <h2 className="text-lg font-semibold">More Categories</h2>
                        </div>
                        <div className="p-2">
                        <ul>
                                <Link to='/financial-tools-calculators/gratuity-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/APY-calculator'>
                                    <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/cagr-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/gst-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                </Link>
                                {/* <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                </Link> */}
                                <Link to='/financial-tools-calculators/inflation-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/salary-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/brokerage-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                </Link>
                                <Link to='/financial-tools-calculators/tds-calculator'>
                                    <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                </Link>
             {/* {moreCategories.map(category => (
       <Link key={category.id} to={`/category/${category.id}`}>
         <li className="py-2 border-b border-gray-300">{category.name}</li>
       </Link>
     ))} */}
           </ul>
         </div>
       </div>

     </div>

   </div>
   </div>
   <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                            <button
                                onClick={() => toggleAccordion(index)}
                                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                            >
                                {faq.question}
                                <Icon id={index} open={activeIndex} />
                            </button>
                            {activeIndex === index && (
                                <div className="p-4 bg-gray-100">
                                    <p className="text-gray-700">{faq.answer}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
   </>
  );
};

export default SbihomeLoanEmiCalculator  ;
